// packages/core/src/diff/processes.ts
import type { ProcessDecl, QualifiedName, StableId } from "../types.js";
import { stableId } from "../types.js";
import type { ResolvedModel, ResolvedModule } from "../resolver.js";
import type { Flow, CallHop } from "../process-model.js";
import { forEachCallHop } from "../process-ir-walk.js";
import type { ProcessDiff, StepKey, Status } from "../diff-types.js";

/**
 * Build a name → stable-ID lookup walking the module tree depth-first.
 * Used so process steps can be keyed on stable module identity rather
 * than the caller's current display name — a module rename shouldn't
 * make every step referencing it look modified. Exported for the
 * scaffold-level diff (process-scaffold-diff.ts).
 */
export function moduleNameIndex(modules: readonly ResolvedModule[]): Map<string, StableId> {
  const out = new Map<string, StableId>();
  function walk(list: readonly ResolvedModule[], parent: readonly string[]) {
    for (const m of list) {
      const path = [...parent, m.name];
      if (m.id) {
        out.set(m.name, m.id);
        // Full dotted path too — a step may reference a nested module by its
        // qualified path (`Platform.Auth.route`), and the rename-proof key
        // must resolve the LONGEST module prefix, not just the head.
        out.set(path.join("."), m.id);
      }
      walk(m.modules, path);
    }
  }
  walk(modules, []);
  return out;
}

/**
 * Project a raw qualified path into a rename-proof key: the head segment
 * resolves through `nameIndex` to its stable ID (falling back to the raw name
 * for actors/unknowns so identical actors still match on both sides); trailing
 * (interface) segments append verbatim. Shared by the hop-level diff below and
 * the scaffold-level diff (process-scaffold-diff.ts).
 */
export function pathStableKey(path: QualifiedName, nameIndex: ReadonlyMap<string, StableId>): string {
  // Longest module-path prefix wins, so `Platform.Auth.route` keys on Auth's
  // stable id — renaming a nested module must not read as an edit.
  for (let k = path.length; k >= 1; k--) {
    const prefix = path.slice(0, k).join(".");
    const id = nameIndex.get(prefix);
    if (id !== undefined) {
      const rest = path.slice(k).join(".");
      return rest ? `${id}.${rest}` : id;
    }
  }
  const rest = path.slice(1).join(".");
  return rest ? `name:${path[0] ?? ""}.${rest}` : `name:${path[0] ?? ""}`;
}

/**
 * Project a call HOP into a stable-ID key (one per edge, chains expanded).
 * The callee path is the WRITTEN form — the module path for an anonymous
 * (unspecified-interface) callee, else the interface path — matching what the
 * author typed.
 */
function stepKeyFromHop(hop: CallHop, nameIndex: Map<string, StableId>): StepKey {
  const to = hop.callee.anonymous ? hop.callee.modulePath : hop.callee.interfacePath;
  return {
    from: pathStableKey(hop.caller, nameIndex),
    to: pathStableKey(to, nameIndex),
  };
}

function keyEquals(a: StepKey, b: StepKey): boolean {
  return a.from === b.from && a.to === b.to;
}

/** Every call hop in the process IR (chains expanded, all controls + reversible
 *  unwinds recursed by the shared `forEachCallHop`), keyed by stable identity. */
export function stepKeys(body: Flow | undefined, nameIndex: Map<string, StableId>): StepKey[] {
  const out: StepKey[] = [];
  forEachCallHop(body, (hop) => out.push(stepKeyFromHop(hop, nameIndex)));
  return out;
}

/** Process IR bodies keyed by stable id (the diff only matches id'd processes). */
function bodyIndex(model: ResolvedModel): Map<StableId, Flow> {
  const out = new Map<StableId, Flow>();
  for (const pm of model.processModels ?? []) if (pm.id) out.set(pm.id, pm.body);
  return out;
}

export function diffProcesses(
  beforeModel: ResolvedModel,
  afterModel: ResolvedModel,
): Map<StableId, ProcessDiff> {
  const beforeIndex = moduleNameIndex(beforeModel.modules);
  const afterIndex = moduleNameIndex(afterModel.modules);
  const beforeBodies = bodyIndex(beforeModel);
  const afterBodies = bodyIndex(afterModel);

  const byIdBefore = new Map<StableId, ProcessDecl>();
  const byIdAfter = new Map<StableId, ProcessDecl>();
  for (const p of beforeModel.processes) if (p.id) byIdBefore.set(p.id, p);
  for (const p of afterModel.processes) if (p.id) byIdAfter.set(p.id, p);

  const out = new Map<StableId, ProcessDiff>();
  const all = new Set<StableId>([...byIdBefore.keys(), ...byIdAfter.keys()]);
  for (const id of all) {
    const b = byIdBefore.get(id);
    const a = byIdAfter.get(id);
    if (b && a) out.set(id, matched(id, b, a, beforeIndex, afterIndex, beforeBodies.get(id), afterBodies.get(id)));
    else if (a) out.set(id, addedProc(id, a, afterIndex, afterBodies.get(id)));
    else if (b) out.set(id, removedProc(id, b, beforeIndex, beforeBodies.get(id)));
  }
  addAnonymousDiffs(out, beforeModel, afterModel, beforeIndex, afterIndex);
  return out;
}

/** Key delimiter for an anonymous step key (from + to). */
const ANON_STEP_SEP = "\x1f";
/** Synthetic diff id namespace for a container's anonymous run (collision-free —
 *  a `#id` can never contain `(`). */
const ANON_ID_PREFIX = "(anon):";

/**
 * Anonymous processes carry NO stable identity — they are positional (spec
 * §4.13.1: an intervening declaration splits one run into two), so a
 * process-identity key would fire phantom `added`/`removed` on untouched steps.
 * They are instead diffed at the EDGE level under the design's anonymous-edge
 * identity: source/target paths keyed within their **owning container**, dropping
 * the deriving-process component. An untouched anonymous run therefore diffs as a
 * no-op (no entry emitted); only a genuine anonymous edge appearing/disappearing
 * produces a diff. Two byte-identical anon runs in one container collapse to one
 * key (benign — spec §4.13.1).
 */
function addAnonymousDiffs(
  out: Map<StableId, ProcessDiff>,
  before: ResolvedModel,
  after: ResolvedModel,
  beforeIndex: Map<string, StableId>,
  afterIndex: Map<string, StableId>,
): void {
  const b = anonEdgesByContainer(before, beforeIndex);
  const a = anonEdgesByContainer(after, afterIndex);
  for (const container of new Set([...b.keys(), ...a.keys()])) {
    const bm = b.get(container) ?? new Map<string, StepKey>();
    const am = a.get(container) ?? new Map<string, StepKey>();
    const added: StepKey[] = [];
    const removed: StepKey[] = [];
    for (const [k, sk] of am) if (!bm.has(k)) added.push(sk);
    for (const [k, sk] of bm) if (!am.has(k)) removed.push(sk);
    if (added.length === 0 && removed.length === 0) continue; // untouched → no phantom
    const status: Status = am.size === 0 ? "removed" : bm.size === 0 ? "added" : "modified";
    const id = stableId(`${ANON_ID_PREFIX}${container}`);
    out.set(id, {
      id,
      name: "",
      status,
      ownerPath: container ? container.split(".") : undefined,
      steps: { added, removed, modified: [], reordered: false },
      sourceFile: anonSourceFile(after, container) ?? anonSourceFile(before, container),
    });
  }
}

/** Every anonymous process's call hops keyed (per owning container) by anon-edge
 *  identity (`from\x1fto`), deduped — the container's anon dependency set. */
function anonEdgesByContainer(model: ResolvedModel, index: Map<string, StableId>): Map<string, Map<string, StepKey>> {
  const out = new Map<string, Map<string, StepKey>>();
  for (const pm of model.processModels ?? []) {
    if (!pm.anonymous) continue;
    const container = (pm.ownerPath ?? []).join(".");
    let m = out.get(container);
    if (!m) {
      m = new Map();
      out.set(container, m);
    }
    forEachCallHop(pm.body, (hop) => {
      const k = stepKeyFromHop(hop, index);
      m!.set(`${k.from}${ANON_STEP_SEP}${k.to}`, k);
    });
  }
  return out;
}

/** Source file of any anonymous process in a container (for change grouping). */
function anonSourceFile(model: ResolvedModel, container: string): string | undefined {
  for (const pm of model.processModels ?? []) {
    if (pm.anonymous && (pm.ownerPath ?? []).join(".") === container) return pm.source?.file;
  }
  return undefined;
}

function pathsEqual(a: readonly string[] | undefined, b: readonly string[] | undefined): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function matched(
  id: StableId,
  b: ProcessDecl,
  a: ProcessDecl,
  beforeIndex: Map<string, StableId>,
  afterIndex: Map<string, StableId>,
  bBody: Flow | undefined,
  aBody: Flow | undefined,
): ProcessDiff {
  const bk = stepKeys(bBody, beforeIndex);
  const ak = stepKeys(aBody, afterIndex);
  const added: StepKey[] = ak.filter((k) => !bk.some((x) => keyEquals(x, k)));
  const removed: StepKey[] = bk.filter((k) => !ak.some((x) => keyEquals(x, k)));
  const reordered =
    added.length === 0 &&
    removed.length === 0 &&
    bk.some((k, i) => !ak[i] || !keyEquals(k, ak[i]));
  const nameChanged = b.name !== a.name;
  const ownerChanged = !pathsEqual(b.ownerPath, a.ownerPath);
  const anyInner = added.length > 0 || removed.length > 0 || reordered;
  let status: Status = "unchanged";
  if (nameChanged || ownerChanged) status = "renamed";
  else if (anyInner) status = "modified";
  return {
    id,
    name: a.name,
    status,
    nameChange: nameChanged ? { from: b.name, to: a.name } : undefined,
    ownerPath: a.ownerPath,
    ownerChange: ownerChanged
      ? { from: b.ownerPath ?? [], to: a.ownerPath ?? [] }
      : undefined,
    steps: { added, removed, modified: [], reordered },
    sourceFile: a.source?.file,
  };
}

function addedProc(id: StableId, p: ProcessDecl, index: Map<string, StableId>, body: Flow | undefined): ProcessDiff {
  return {
    id,
    name: p.name,
    status: "added",
    ownerPath: p.ownerPath,
    steps: {
      added: stepKeys(body, index),
      removed: [],
      modified: [],
      reordered: false,
    },
    sourceFile: p.source?.file,
  };
}

function removedProc(id: StableId, p: ProcessDecl, index: Map<string, StableId>, body: Flow | undefined): ProcessDiff {
  return {
    id,
    name: p.name,
    status: "removed",
    ownerPath: p.ownerPath,
    steps: {
      added: [],
      removed: stepKeys(body, index),
      modified: [],
      reordered: false,
    },
    sourceFile: p.source?.file,
  };
}
